import { isMissing } from "./type-registry";
// Function registry with validation and discovery
export class FunctionRegistry {
    functions = new Map(); // Support overloading
    register(fn) {
        const name = fn.signature.name;
        if (!this.functions.has(name)) {
            this.functions.set(name, []);
        }
        this.functions.get(name).push(fn);
    }
    resolve(name, args) {
        const implementations = this.functions.get(name);
        if (!implementations)
            return null;
        // Find the best matching implementation
        for (const impl of implementations) {
            if (this.isSignatureMatch(impl.signature, args)) {
                return impl;
            }
        }
        return null;
    }
    validate(name, args) {
        const implementations = this.functions.get(name);
        if (!implementations) {
            return {
                type: "FunctionNotFound",
                message: `Function "${name}" not found.`,
            };
        }
        // Check if any implementation matches
        for (const impl of implementations) {
            if (this.isSignatureMatch(impl.signature, args)) {
                return null; // Valid
            }
        }
        // Generate error with expected signatures
        const signatures = implementations.map((impl) => this.formatSignature(impl.signature));
        return {
            type: "WrongNumberOfArguments",
            message: `Function "${name}" called with ${args.length} arguments, but expected one of: ${signatures.join(", ")}`,
        };
    }
    /**
     * Positions of `lazy` parameters across all overloads of a function name.
     * The evaluator uses this to decide which argument expressions to defer
     * (pass as raw AST) rather than pre-evaluate.
     */
    lazyParamPositions(name) {
        const positions = new Set();
        const impls = this.functions.get(name);
        if (!impls)
            return positions;
        for (const impl of impls) {
            impl.signature.parameters.forEach((p, i) => {
                if (p.lazy)
                    positions.add(i);
            });
        }
        return positions;
    }
    getAvailable() {
        const allSignatures = [];
        for (const implementations of this.functions.values()) {
            for (const impl of implementations) {
                allSignatures.push(impl.signature);
            }
        }
        return allSignatures;
    }
    getByCategory(category) {
        return this.getAvailable().filter((sig) => sig.category === category);
    }
    isSignatureMatch(signature, args) {
        const params = signature.parameters;
        const lastParam = params[params.length - 1];
        const isVariadic = !!lastParam?.variadic;
        if (isVariadic) {
            const leading = params.slice(0, -1);
            const requiredLeading = leading.filter((p) => !p.optional).length;
            const minTrailing = lastParam.optional ? 0 : 1;
            // Variadic absorbs an unbounded tail — only a lower bound applies.
            if (args.length < requiredLeading + minTrailing) {
                return false;
            }
            for (let i = 0; i < args.length; i++) {
                const param = i < leading.length ? leading[i] : lastParam;
                const arg = args[i];
                if (!this.isTypeMatch(param.type, arg.type))
                    return false;
                if (param.validator && !param.validator(arg))
                    return false;
            }
            return true;
        }
        const requiredParams = params.filter((p) => !p.optional);
        const totalParams = params.length;
        // Check argument count
        if (args.length < requiredParams.length || args.length > totalParams) {
            return false;
        }
        // Check argument types
        for (let i = 0; i < args.length; i++) {
            const param = params[i];
            const arg = args[i];
            if (!this.isTypeMatch(param.type, arg.type)) {
                return false;
            }
            // Run custom validator if provided
            if (param.validator && !param.validator(arg)) {
                return false;
            }
        }
        return true;
    }
    isTypeMatch(expectedType, actualType) {
        // `missing` is structurally compatible with any parameter — it only ever
        // arises from empty cells / propagation, never from a genuine type error
        // (those are caught at save-time validation). Accepting it here lets the
        // argument reach the call site, where the evaluator skips (aggregates) or
        // propagates (everything else).
        if (actualType === "missing")
            return true;
        // A `deferred` argument (raw AST for a lazy param) is only ever produced by
        // the evaluator for a `predicate`/`column_ref` slot, so it matches those.
        if (actualType === "deferred") {
            const expected = typeof expectedType === "string" ? [expectedType] : expectedType;
            return (expected.includes("predicate") ||
                expected.includes("column_ref") ||
                expected.includes("any"));
        }
        if (typeof expectedType === "string") {
            return expectedType === actualType || expectedType === "any";
        }
        return expectedType.includes(actualType) || expectedType.includes("any");
    }
    formatSignature(signature) {
        const params = signature.parameters.map((p) => {
            const typeStr = typeof p.type === "string" ? p.type : p.type.join("|");
            if (p.variadic)
                return `...${p.name}: ${typeStr}`;
            return p.optional ? `${p.name}?: ${typeStr}` : `${p.name}: ${typeStr}`;
        });
        return `${signature.name}(${params.join(", ")})`;
    }
}
// Phase 2: Base Function Classes
// Base class for aggregate functions (sum, avg, min, max)
export class AggregateFunction {
    // Aggregates take 1+ arguments, each a scalar or an array, flattened into a
    // single numeric list and reduced. They receive `missing` arguments and skip
    // them (rather than the evaluator propagating to `missing`).
    handlesMissing = true;
    execute(args, context) {
        const validation = this.validateArgs(args, context);
        if (validation)
            return validation;
        // Flatten: drop `missing` args, spread arrays, wrap scalars. Array elements
        // and scalars share the `{ value, unit }` shape the aggregators consume.
        const elements = [];
        for (const arg of args) {
            if (isMissing(arg))
                continue;
            if (arg.type === "array") {
                for (const el of arg.value)
                    elements.push(el);
            }
            else {
                elements.push(arg);
            }
        }
        return this.aggregate(elements, context);
    }
    validateArgs(args, context) {
        if (args.length < 1) {
            return context.createError("WrongNumberOfArguments", `Function "${this.signature.name}" expects at least 1 argument, but received ${args.length}.`);
        }
        for (const arg of args) {
            if (!isMissing(arg) &&
                arg.type !== "scalar" &&
                arg.type !== "array") {
                return context.createError("InvalidArgumentType", `Function "${this.signature.name}" expects scalar or array arguments, but received type "${arg.type}".`);
            }
        }
        return null;
    }
}
// Base class for table operations
export class TableFunction {
    execute(args, context) {
        const validation = this.validateTableArgs(args, context);
        if (validation)
            return validation;
        return this.processTable(args[0], args.slice(1), context);
    }
    validateTableArgs(args, context) {
        if (args.length === 0) {
            return context.createError("WrongNumberOfArguments", `Function "${this.signature.name}" expects at least 1 argument (table), but received ${args.length}.`);
        }
        if (args[0].type !== "table") {
            return context.createError("InvalidArgumentType", `Function "${this.signature.name}" expects a table as first argument, but received type "${args[0].type}".`);
        }
        return null;
    }
}
// Global function registry instance
export const globalFunctionRegistry = new FunctionRegistry();
//# sourceMappingURL=function-registry.js.map